import { CenterSpinner } from '@/components/ats';
import kulaLogo from '@/assets/icons/Logo.svg';
import { Box, Flex, Image, Link } from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import { useGenerateScorecardApi, useScorecardNetworkIdle } from './hooks';
import {
  ScorecardPDFHeader,
  ScorecardPDFSummary,
  ScorecardPDFHistory,
  ScorecardPDFItem,
} from './components';
import './printStyles.css';
import { ScorecardItem } from './GenerateScorecard.interface';
import { useMemo } from 'react';
import { useKFlags } from '@/hooks/use-k-flags';
import { dateFormats } from '@/hooks/useDateFormat';

declare global {
  interface Window {
    reportPdfGeneration: string;
  }
}

const getActivityTypeLabel = (recordType: string) => {
  if (recordType === 'AtsInterview') return 'Interview';
  if (recordType === 'AtsReview') return 'Review';
  if (recordType === 'AtsAssessment') return 'Assessment';
  return 'Activity';
};

/** Section title above each scorecard detail: "Interview — Panel call" (no grouping count). */
const getPdfSectionTitle = (item: ScorecardItem) => {
  const typeLabel = getActivityTypeLabel(item.record_type);
  const recordName = item.record?.name?.trim();
  return recordName ? `${typeLabel} - ${recordName}` : typeLabel;
};

interface StageGroup {
  stageId: number;
  stageName: string;
  items: ScorecardItem[];
}

const GenerateScorecard = () => {
  const { loading, error, scorecards, params, applicationData, createdBy, aiSummary, userPrefs } =
    useGenerateScorecardApi();

  const { feApplicationAssistant } = useKFlags();

  // Resolve timezone and date-time format from userPrefs fetched after token exchange.
  // This avoids the Redux bootstrap timing issue where currUser is never loaded in Gotenberg.
  // URL params take priority (for when the backend eventually passes them to Gotenberg).
  const resolvedTimezone = params?.tz || userPrefs?.time_zone || undefined;
  const resolvedDateTimeFormat = useMemo(() => {
    if (params.date_time_format) return params.date_time_format;
    const fmt = dateFormats.find((f) => f.value === userPrefs?.date_fmt) ?? dateFormats[0];
    const timePart = userPrefs?.time_fmt === 'hour_12' ? 'hh:mm A' : 'HH:mm';
    return `${fmt.label} [at] ${timePart}`;
  }, [params.date_time_format, userPrefs?.date_fmt, userPrefs?.time_fmt]);

  // Signal Gotenberg only when data is loaded AND network is idle.
  // Passing `loading` prevents Gotenberg from capturing the spinner.
  useScorecardNetworkIdle(loading);

  // Group by stage. Order matches application scorecard: scorecards[] is already stage-sorted;
  // preserve per-stage row order and sort stage groups by pipeline position (not raw stage id).
  const stagesWiseData: StageGroup[] = useMemo(() => {
    const stageMap = new Map<number, StageGroup>();

    const filteredScorecards = scorecards.filter(
      (scorecard) => scorecard.status === 'submitted'
    );

    for (const item of filteredScorecards) {
      const stageId = item.stage.id;
      let stage = stageMap.get(stageId);
      if (!stage) {
        stage = {
          stageId,
          stageName: item.stage.name,
          items: [],
        };
        stageMap.set(stageId, stage);
      }
      stage.items.push(item);
    }

    const stagePosition = (item: ScorecardItem) =>
      (item as ScorecardItem & { ats_job_stage?: { position?: number } })
        ?.ats_job_stage?.position ?? item.stage.id;

    return Array.from(stageMap.values())
      .sort((a, b) => {
        const posA = stagePosition(a.items[0]);
        const posB = stagePosition(b.items[0]);
        if (posA !== posB) return posA - posB;
        return a.stageId - b.stageId;
      })
      .map((stage) => ({ ...stage, items: stage.items }));
  }, [scorecards]);

  // Loading state
  if (loading) {
    return <CenterSpinner h="90vh" />;
  }

  // Error state
  if (error) {
    return (
      <Flex
        h="90vh"
        flexDir="column"
        gap="12px"
        alignItems="center"
        justifyContent="center"
      >
        <KText size="lg-semi-bold" color="error-600">
          Error Loading Scorecards
        </KText>
        <KText size="sm" color="secondary-600">
          {error}
        </KText>
        <KText size="sm" color="secondary-500">
          Please contact us at{' '}
          <Link href="mailto:support@kula.ai" color="primary-600">
            support@kula.ai
          </Link>
        </KText>
      </Flex>
    );
  }

  // Empty state
  if (!stagesWiseData?.length) {
    return (
      <Flex
        h="90vh"
        flexDir="column"
        gap="12px"
        alignItems="center"
        justifyContent="center"
      >
        <KText size="lg-semi-bold" color="secondary-700">
          No Submitted Scorecards
        </KText>
        <KText size="sm" color="secondary-500">
          There are no submitted scorecards available for this application.
        </KText>
      </Flex>
    );
  }

  // Get candidate info from application data or fallback to first scorecard
  const candidate = applicationData?.contact || scorecards[0]?.candidate;
  const jobTitle = applicationData?.ats_job?.name || '';

  return (
    <Flex
      flexDir="column"
      overflowY="auto"
      bg="base-white"
      id="divToPrint"
      className="scorecard-pdf-container"
      h="100%"
      minH={0}
      w="950px"
      position="relative"
    >
      <Image
        src={kulaLogo}
        alt=""
        aria-hidden
        className="scorecard-pdf-watermark"
      />
      <Flex
        flexDir="column"
        w="100%"
        mx="auto"
        className="scorecard-pdf-inner"
        position="relative"
        zIndex={1}
      >
        {/* Header with candidate info */}
        <ScorecardPDFHeader
          candidate={candidate}
          jobTitle={jobTitle}
          createdBy={createdBy}
        />

        {feApplicationAssistant && <ScorecardPDFSummary summary={aiSummary} />}

        {/* History table */}
        <ScorecardPDFHistory scorecards={scorecards} />

        {/* Stages — section title is inside ScorecardPDFItem (lead cluster) for print page-break behavior */}
        <Flex flexDir="column" w="100%" className="scorecard-pdf-stages">
          {stagesWiseData.map((stage) => (
            <Flex
              key={stage.stageId}
              flexDir="column"
              gap="24px"
              mt="24px"
              className="stage-group"
            >
              <Flex alignItems="center" gap="12px" className="section-header">
                <KText
                  size="lg-semi-bold"
                  borderBottom="1px solid"
                  borderColor="border-color"
                  pb="16px"
                  flex={1}
                >
                  {stage.stageName}
                </KText>
              </Flex>

              <Flex flexDir="column" gap="28px" className="stage-items">
                {stage.items.map((item) => (
                  <Box key={item.id} className="scorecard-detail-block">
                    <ScorecardPDFItem
                      scorecard={item}
                      index={0}
                      timezone={resolvedTimezone}
                      dateTimeFormat={resolvedDateTimeFormat}
                      hideActivityName
                      pdfSectionTitle={getPdfSectionTitle(item)}
                    />
                  </Box>
                ))}
              </Flex>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default GenerateScorecard;
